// The partner application's seven fields and their checks: the same rules as
// REGISTRATION_RULES and HEARD_ABOUT_OPTIONS in apps/server/src/partners.mjs, which the
// partner bundle may not import (partnerBundle.test.ts). registration.test.ts holds the
// two to the same labels, limits and options, so a change on one side fails the build.

export const HEARD_ABOUT_OPTIONS = ['Someone at Olytic', 'Another Olytic partner', 'An event', 'LinkedIn', 'Search', 'Other'] as const;

export type RegistrationKey = 'firstName' | 'lastName' | 'companyName' | 'companyWebsite' | 'phone' | 'heardAbout' | 'partnershipVision';

export interface RegistrationRule {
  key: RegistrationKey;
  label: string;
  required: boolean;
  max: number;
  options?: readonly string[];
}

/** In the order the form shows them. */
export const REGISTRATION_FIELDS: readonly RegistrationRule[] = [
  { key: 'firstName', label: 'First Name', required: true, max: 100 },
  { key: 'lastName', label: 'Last Name', required: true, max: 100 },
  { key: 'companyName', label: 'Company', required: true, max: 200 },
  { key: 'companyWebsite', label: 'Website', required: false, max: 300 },
  { key: 'phone', label: 'Phone', required: false, max: 200 },
  { key: 'heardAbout', label: 'How You Heard About Us', required: true, max: 300, options: HEARD_ABOUT_OPTIONS },
  { key: 'partnershipVision', label: 'How You See the Partnership Working', required: true, max: 2000 },
];

/** What the person has typed, one string per field ('' when left empty). */
export type RegistrationValues = Record<RegistrationKey, string>;

/** One message per failing field, keyed as the API keys them. */
export type FieldErrors = Partial<Record<string, string>>;

export function emptyRegistration(): RegistrationValues {
  return { firstName: '', lastName: '', companyName: '', companyWebsite: '', phone: '', heardAbout: '', partnershipVision: '' };
}

/**
 * The API's checks, in its order: required, then length (after trimming), then the
 * option list, with its messages word for word. An empty object means the form may be sent.
 */
export function validateRegistration(values: RegistrationValues): FieldErrors {
  const errors: FieldErrors = {};
  for (const rule of REGISTRATION_FIELDS) {
    const text = values[rule.key].trim();
    if (text === '') {
      if (rule.required) errors[rule.key] = `${rule.label} is required.`;
    } else if (text.length > rule.max) {
      errors[rule.key] = `${rule.label} must be ${rule.max.toLocaleString('en-US')} characters or fewer.`;
    } else if (rule.options && !rule.options.includes(text)) {
      errors[rule.key] = `${rule.label} must be one of the listed options.`;
    }
  }
  return errors;
}

/**
 * The POST /api/partner/registration body: exactly the seven keys, trimmed. Never an
 * email (the server takes it from the session), a status or a consent.
 */
export function registrationBody(values: RegistrationValues): RegistrationValues {
  const body = emptyRegistration();
  for (const rule of REGISTRATION_FIELDS) body[rule.key] = values[rule.key].trim();
  return body;
}
